import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Rocket, Shield, Map, LayoutGrid, Zap, Globe, TrendingUp, Users, CheckCircle, ChevronRight, Menu, X, Phone, Mail, Linkedin, Instagram, Box, Cpu, Database, Network, Eye } from 'lucide-react';
import { PROBLEMS, SOLUTIONS, MARKET_STATS } from './constants';
import { submitEarlyAccess } from './services/supabase';
import SectionHeading from './components/SectionHeading';
import Roadmap from './components/Roadmap';
import Assistant from './components/Assistant';
import HeroBackground from './components/HeroBackground';
import PitchDeckButton from './components/PitchDeckButton';
import LegalModal from './components/LegalModal';

const LOGO_URL = "https://lh3.googleusercontent.com/d/1kjGqmZ1d6gOOgjij1zV-YevADEsZ0OM3";

const TechOrbit = () => {
  const nodes = [
    { icon: <Shield className="w-6 h-6" />, label: "Security", angle: 0 },
    { icon: <Map className="w-6 h-6" />, label: "Wayfinding", angle: 90 },
    { icon: <Database className="w-6 h-6" />, label: "Data", angle: 180 },
    { icon: <Network className="w-6 h-6" />, label: "Connectivity", angle: 270 },
  ];

  return (
    <div className="relative w-full h-full flex items-center justify-center scale-50 md:scale-100">
      <motion.div
        animate={{ 
          scale: [1, 1.1, 1],
          boxShadow: [
            "0 0 40px rgba(225, 29, 72, 0.2)",
            "0 0 80px rgba(225, 29, 72, 0.5)",
            "0 0 40px rgba(225, 29, 72, 0.2)"
          ]
        }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        className="relative z-20 w-32 h-32 rounded-full bg-gradient-to-br from-rose-500 to-rose-900 flex items-center justify-center border border-white/20 overflow-hidden"
      >
        <img src={LOGO_URL} alt="Logo" className="w-20 h-20 object-contain brightness-0 invert opacity-90" 
             onError={(e) => { (e.target as HTMLImageElement).src = 'https://api.dicebear.com/7.x/shapes/svg?seed=robot'; }} />
        <Cpu className="absolute w-14 h-14 text-white/20 animate-pulse pointer-events-none" />
        <div className="absolute inset-0 rounded-full border-2 border-rose-500/30 animate-ping" />
      </motion.div>

      <div className="absolute w-[400px] h-[400px] border border-white/5 rounded-full" />
      <div className="absolute w-[250px] h-[250px] border border-white/10 rounded-full" />

      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
        className="absolute w-[400px] h-[400px]"
      >
        {nodes.map((node, i) => (
          <motion.div
            key={i}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
            style={{
              transform: `rotate(${node.angle}deg) translate(200px) rotate(-${node.angle}deg)`
            }}
          >
            <motion.div
              animate={{ rotate: -360 }}
              transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
              whileHover={{ scale: 1.2 }}
              className="w-16 h-16 rounded-2xl glass-card border-rose-500/20 flex flex-col items-center justify-center text-rose-500 group cursor-pointer shadow-[0_10px_30px_rgba(0,0,0,0.5)]"
            >
              {node.icon}
              <span className="absolute -bottom-8 opacity-0 group-hover:opacity-100 transition-opacity text-[10px] font-black uppercase tracking-widest text-white whitespace-nowrap">
                {node.label}
              </span>
            </motion.div>
          </motion.div>
        ))}
      </motion.div>

      <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-20">
        <motion.circle
          cx="50%" cy="50%" r="200"
          fill="none"
          stroke="url(#grad)"
          strokeWidth="1"
          strokeDasharray="10 20"
          animate={{ strokeDashoffset: [0, -100] }}
          transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
        />
        <defs>
          <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#f43f5e" />
            <stop offset="100%" stopColor="#881337" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
};

const SpotlightCard: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => {
  const cardRef = useRef<HTMLDivElement>(null);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    cardRef.current.style.setProperty("--mouse-x", `${x}px`);
    cardRef.current.style.setProperty("--mouse-y", `${y}px`);
  };

  return (
    <div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      className={`spotlight-card glass-card ${className}`}
    >
      {children}
    </div>
  );
};

const App: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeLegalModal, setActiveLegalModal] = useState<'privacy' | 'terms' | null>(null);
  const [formState, setFormState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formData, setFormData] = useState({ college: '', contact: '', email: '', message: '' });

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      window.scrollTo({
        top: elementRect - bodyRect - offset,
        behavior: 'smooth'
      });
    }
    setMobileMenuOpen(false);
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormState('loading');
    try {
      await submitEarlyAccess({
        college_name: formData.college,
        contact_person: formData.contact,
        email: formData.email,
        message: formData.message
      });
      setFormState('success');
      setFormData({ college: '', contact: '', email: '', message: '' });
      setTimeout(() => setFormState('idle'), 6000);
    } catch (err) {
      setFormState('error');
      setTimeout(() => setFormState('idle'), 3000);
    }
  };

  const privacyContent = (
    <div className="space-y-6">
      <section>
        <h4 className="text-white font-black uppercase mb-4 text-rose-500">1. Data Ingestion</h4>
        <p>Heliophania Anamorphe collects institutional data provided through our Early Access protocol, including contact names, official emails, and campus specifications. This data is exclusively utilized to establish pilot partnerships.</p>
      </section>
      <section>
        <h4 className="text-white font-black uppercase mb-4 text-rose-500">2. AI Interaction Logs</h4>
        <p>Interaction data with the Heliophania Robot Assistant is recorded to refine our cognitive mapping and natural language processing layers. No personal identifiable information is extracted from these sessions beyond what the user explicitly provides.</p>
      </section>
      <section>
        <h4 className="text-white font-black uppercase mb-4 text-rose-500">3. Third-Party Governance</h4>
        <p>We utilize enterprise-grade infrastructure (Supabase, Google Cloud) for data persistence. We do not sell or trade institutional data to external market entities.</p>
      </section>
    </div>
  );

  const termsContent = (
    <div className="space-y-6">
      <section>
        <h4 className="text-white font-black uppercase mb-4 text-rose-500">1. Operational Status</h4>
        <p>The services and robotics ecosystem presented on this interface are in an active development and pilot phase. Operational parameters may shift as field-testing progresses.</p>
      </section>
      <section>
        <h4 className="text-white font-black uppercase mb-4 text-rose-500">2. Intellectual Property</h4>
        <p>All robotic hardware designs, autonomous navigation algorithms, and spatial AI architectures displayed or described are the exclusive property of Heliophania Anamorphe Pvt Ltd.</p>
      </section>
      <section>
        <h4 className="text-white font-black uppercase mb-4 text-rose-500">3. Usage Restrictions</h4>
        <p>The AI Assistant interface and early access forms must not be used for malicious injection, data scraping, or unauthorized system probing. Any breach of security protocols will result in immediate termination of institutional access.</p>
      </section>
    </div>
  );

  return (
    <div className="min-h-screen relative selection:bg-rose-500/30">
      <div className="fixed inset-0 bg-[#020205] -z-20" />
      
      <nav className={`fixed top-0 w-full z-50 transition-all duration-700 ${isScrolled ? 'bg-[#020205]/60 backdrop-blur-2xl py-3 md:py-4 border-b border-white/5' : 'bg-transparent py-6 md:py-8'}`}>
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center group cursor-pointer" 
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <div className="w-10 h-10 md:w-12 md:h-12 bg-rose-600/10 border border-rose-500/20 rounded-xl flex items-center justify-center shadow-[0_0_30px_rgba(225,29,72,0.1)] group-hover:scale-110 transition-all duration-500 overflow-hidden">
               <img src={LOGO_URL} alt="Heliophania" className="w-8 h-8 md:w-10 md:h-10 object-contain brightness-0 invert" 
                    onError={(e) => { (e.target as HTMLImageElement).src = 'https://api.dicebear.com/7.x/shapes/svg?seed=robot'; }} />
            </div>
          </motion.div>

          <div className="hidden md:flex items-center gap-12 text-[11px] font-bold tracking-[0.25em] uppercase text-gray-400">
            {['Problem', 'Solution', 'Market', 'Roadmap'].map((item) => (
              <a 
                key={item} 
                href={`#${item.toLowerCase()}`} 
                onClick={(e) => scrollToSection(e, item.toLowerCase())}
                className="hover:text-rose-500 transition-colors"
              >
                {item}
              </a>
            ))}
            <a href="#early" onClick={(e) => scrollToSection(e, 'early')}>
              <button className="bg-white text-black hover:bg-rose-600 hover:text-white px-8 py-3 rounded-full transition-all duration-500 font-extrabold text-[10px] tracking-[0.25em]">
                EARLY ACCESS
              </button>
            </a>
          </div>

          <button className="md:hidden text-white p-2" onClick={() => setMobileMenuOpen(true)}>
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-[60] bg-[#020205]/95 backdrop-blur-3xl p-10 flex flex-col items-center justify-center gap-10"
          >
            <button className="absolute top-10 right-10 text-white" onClick={() => setMobileMenuOpen(false)}>
              <X className="w-8 h-8" />
            </button>
            {['Problem', 'Solution', 'Market', 'Roadmap'].map((item) => (
              <a 
                key={item} 
                href={`#${item.toLowerCase()}`} 
                onClick={(e) => scrollToSection(e, item.toLowerCase())} 
                className="text-3xl font-black uppercase tracking-tighter text-white hover:text-rose-500 transition-colors"
              >
                {item}
              </a>
            ))}
            <a href="#early" onClick={(e) => scrollToSection(e, 'early')}>
              <button className="bg-rose-600 text-lg font-black px-10 py-4 rounded-2xl text-white">
                Get Access
              </button>
            </a>
          </motion.div>
        )}
      </AnimatePresence>

      <section className="relative min-h-screen flex items-center justify-center px-6 py-24 md:py-0 overflow-hidden">
        <HeroBackground />
        
        <div className="max-w-6xl w-full text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="inline-flex items-center gap-3 px-4 py-1.5 rounded-full bg-rose-500/5 border border-rose-500/10 text-rose-400 text-[9px] md:text-[10px] font-extrabold tracking-[0.3em] uppercase mb-10 md:mb-12">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-rose-500"></span>
              </span>
              India's Autonomous Robotics Ecosystem
            </div>
            
            <h1 className="text-5xl md:text-[10rem] font-[900] tracking-tighter leading-[0.9] md:leading-[0.8] mb-10 md:mb-12 text-white">
              The Future of <br />
              <span className="text-gradient">Campus Life</span>
            </h1>
            
            <p className="text-lg md:text-3xl text-gray-400 max-w-2xl md:max-w-3xl mx-auto mb-12 md:mb-16 font-medium leading-relaxed opacity-80">
              Transforming institutional physical environments with AI-native navigation, smart security, and spatial intelligence.
            </p>

            <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-10">
              <a href="#early" onClick={(e) => scrollToSection(e, 'early')}>
                <motion.button 
                  whileHover={{ scale: 1.05, y: -4 }}
                  whileTap={{ scale: 0.96 }}
                  className="group relative bg-rose-600 text-white font-black text-lg md:text-xl h-14 md:h-20 px-10 md:px-14 rounded-2xl md:rounded-3xl transition-all duration-500 flex items-center gap-4 overflow-hidden shadow-[0_20px_50px_-15px_rgba(225,29,72,0.4)]"
                >
                  <span className="relative z-10">Secure Your Slot</span>
                  <ChevronRight className="relative z-10 w-6 h-6 md:w-7 md:h-7 group-hover:translate-x-1.5 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </motion.button>
              </a>
              
              <PitchDeckButton />
            </div>
          </motion.div>
        </div>
      </section>

      <section id="problem" className="py-24 md:py-48 px-6">
        <div className="max-w-7xl mx-auto">
          <SectionHeading 
            title="The Institutional Gap" 
            subtitle="Inefficient infrastructure and manual tracking are silent blockers to institutional growth."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {PROBLEMS.map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                viewport={{ once: true }}
              >
                <SpotlightCard className="p-8 md:p-12 rounded-[2.5rem] md:rounded-[3.5rem] h-full group">
                  <div className="mb-8 md:mb-10 p-4 md:p-5 w-fit bg-rose-500/5 rounded-xl md:rounded-2xl group-hover:bg-rose-500/15 group-hover:scale-110 transition-all duration-500 text-rose-500">
                    {p.icon}
                  </div>
                  <h3 className="text-xl md:text-2xl font-black mb-4 md:mb-6 text-white tracking-tight">{p.title}</h3>
                  <p className="text-gray-500 text-sm md:text-base leading-relaxed font-medium">{p.description}</p>
                </SpotlightCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="solution" className="py-24 md:py-48 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-rose-600/[0.015] -z-10" />
        <div className="max-w-7xl mx-auto">
          <SectionHeading 
            centered
            title="Precision Engineering" 
            subtitle="Modular, AI-native systems designed specifically for modern institutional complexities."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
            {SOLUTIONS.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                whileHover={{ y: -10, scale: 1.02 }}
                transition={{ type: "spring", stiffness: 200, damping: 25 }}
                className="glass-card p-10 md:p-14 rounded-[3rem] md:rounded-[4rem] text-center border-b-4 md:border-b-8 border-b-rose-600/20 shadow-xl md:shadow-2xl"
              >
                <div className="text-5xl md:text-6xl mb-8 md:mb-10 filter grayscale brightness-125 hover:grayscale-0 transition-all cursor-default duration-500">{s.icon}</div>
                <h3 className="text-2xl md:text-3xl font-black mb-4 md:mb-6 text-white uppercase tracking-tighter">{s.title}</h3>
                <p className="text-gray-500 leading-relaxed font-bold text-xs md:text-sm tracking-wide">{s.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="market" className="py-24 md:py-48 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 md:gap-32 items-center">
            <div>
              <SectionHeading 
                title="Scaling Potential" 
                subtitle="The autonomous service robot market is reaching a critical inflection point globally."
              />
              <div className="grid grid-cols-2 gap-4 md:gap-6">
                {MARKET_STATS.map((stat, i) => (
                  <SpotlightCard key={i} className="p-8 md:p-12 rounded-[2rem] md:rounded-[2.5rem] flex flex-col items-start hover:border-rose-500/40">
                    <div className="text-rose-500 mb-6 md:mb-8">{stat.icon}</div>
                    <div className="text-3xl md:text-5xl font-black text-white mb-2 md:mb-3 tracking-tighter">{stat.value}</div>
                    <div className="text-[9px] md:text-[10px] font-extrabold text-gray-500 uppercase tracking-[0.3em]">{stat.label}</div>
                  </SpotlightCard>
                ))}
              </div>
            </div>
            <motion.div 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 1.5 }}
              className="relative aspect-square flex items-center justify-center max-h-[500px] md:max-h-none"
            >
              <TechOrbit />
            </motion.div>
          </div>
        </div>
      </section>

      <section id="roadmap" className="py-24 md:py-48 bg-black">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading 
            centered
            title="Strategic Horizon" 
            subtitle="From initial validation to nationwide deployment."
          />
          <Roadmap />
        </div>
      </section>

      <section id="early" className="py-24 md:py-48 px-6 relative">
        <div className="max-w-4xl mx-auto">
          <motion.div 
             initial={{ opacity: 0, scale: 0.98 }}
             whileInView={{ opacity: 1, scale: 1 }}
             transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
             className="glass-card p-8 md:p-24 rounded-[3rem] md:rounded-[5rem] border-rose-500/10 shadow-[0_0_100px_rgba(225,29,72,0.1)] text-center relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-rose-600/5 blur-[80px] rounded-full -translate-y-1/2 translate-x-1/2" />
            <h2 className="text-4xl md:text-8xl font-black mb-6 md:mb-8 text-white tracking-tighter">Join the Wave</h2>
            <p className="text-gray-400 mb-12 md:mb-20 text-lg md:text-xl font-medium max-w-xl mx-auto opacity-80">Selected partners gain early pilot access.</p>
            
            <form onSubmit={handleFormSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 text-left">
              <div className="flex flex-col gap-2">
                <label className="text-[9px] md:text-[10px] font-black uppercase tracking-widest text-rose-500/60 ml-2">Institution</label>
                <input 
                  required
                  value={formData.college}
                  onChange={e => setFormData({...formData, college: e.target.value})}
                  type="text" 
                  placeholder="e.g. IIT Madras" 
                  className="bg-white/5 border border-white/10 rounded-xl md:rounded-2xl p-4 md:p-6 focus:outline-none focus:border-rose-600 transition-all duration-300 text-white placeholder:text-gray-700 text-sm md:text-base"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-[9px] md:text-[10px] font-black uppercase tracking-widest text-rose-500/60 ml-2">Contact Name</label>
                <input 
                  required
                  value={formData.contact}
                  onChange={e => setFormData({...formData, contact: e.target.value})}
                  type="text" 
                  placeholder="Admin Head" 
                  className="bg-white/5 border border-white/10 rounded-xl md:rounded-2xl p-4 md:p-6 focus:outline-none focus:border-rose-600 transition-all duration-300 text-white placeholder:text-gray-700 text-sm md:text-base"
                />
              </div>
              <div className="md:col-span-2 flex flex-col gap-2">
                <label className="text-[9px] md:text-[10px] font-black uppercase tracking-widest text-rose-500/60 ml-2">Official Email</label>
                <input 
                  required
                  value={formData.email}
                  onChange={e => setFormData({...formData, email: e.target.value})}
                  className="bg-white/5 border border-white/10 rounded-xl md:rounded-2xl p-4 md:p-6 focus:outline-none focus:border-rose-600 transition-all duration-300 text-white placeholder:text-gray-700 text-sm md:text-base"
                  type="email" 
                  placeholder="admin@college.edu.in" 
                />
              </div>
              <div className="md:col-span-2 flex flex-col gap-2">
                <label className="text-[9px] md:text-[10px] font-black uppercase tracking-widest text-rose-500/60 ml-2">Institutional Scale</label>
                <textarea 
                  value={formData.message}
                  onChange={e => setFormData({...formData, message: e.target.value})}
                  className="bg-white/5 border border-white/10 rounded-xl md:rounded-2xl p-4 md:p-6 focus:outline-none focus:border-rose-600 transition-all duration-300 text-white placeholder:text-gray-700 text-sm md:text-base"
                  rows={4} 
                  placeholder="Campus size or automation needs..." 
                />
              </div>
              <motion.button 
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit" 
                disabled={formState === 'loading'}
                className="md:col-span-2 bg-white text-black py-5 md:py-7 rounded-2xl md:rounded-3xl font-black text-xl md:text-2xl hover:bg-rose-600 hover:text-white transition-all duration-500 shadow-xl disabled:opacity-50"
              >
                {formState === 'loading' ? "Processing..." : "Request Exclusive Pilot"}
              </motion.button>
            </form>
          </motion.div>
        </div>
      </section>

      <footer className="py-24 md:py-40 border-t border-white/5 bg-[#010103]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-16 md:gap-24 mb-20 md:mb-32">
            <div className="col-span-1 md:col-span-2">
               <div className="w-16 h-16 md:w-20 md:h-20 bg-rose-600/10 border border-rose-500/20 rounded-2xl flex items-center justify-center mb-8 md:mb-12 shadow-2xl overflow-hidden">
                 <img src={LOGO_URL} alt="Heliophania" className="w-10 h-10 md:w-14 md:h-14 object-contain brightness-0 invert" 
                      onError={(e) => { (e.target as HTMLImageElement).src = 'https://api.dicebear.com/7.x/shapes/svg?seed=robot'; }} />
               </div>
               <p className="text-gray-500 text-lg md:text-xl leading-relaxed max-w-sm font-semibold opacity-80">
                 Redefining physical campus experiences through the fusion of autonomous robotics and spatial AI.
               </p>
            </div>
            <div>
              <h4 className="text-white font-black mb-8 md:mb-10 tracking-[0.3em] text-[10px] uppercase">Direct Connect</h4>
              <div className="space-y-6">
                <a href="tel:+919622927636" className="text-gray-500 hover:text-rose-500 flex items-center gap-4 transition-all duration-300 font-bold group">
                  <div className="p-2.5 rounded-xl bg-white/5 group-hover:bg-rose-500/20 transition-colors">
                    <Phone className="w-4 h-4" />
                  </div>
                  +91 9622927636
                </a>
                <a href="mailto:heliophania.anamorphe@proton.me" className="text-gray-500 hover:text-rose-500 flex items-center gap-4 transition-all duration-300 font-bold group truncate max-w-full">
                  <div className="p-2.5 rounded-xl bg-white/5 group-hover:bg-rose-500/20 transition-colors flex-shrink-0">
                    <Mail className="w-4 h-4" />
                  </div>
                  <span className="truncate">heliophania.anamorphe@proton.me</span>
                </a>
              </div>
            </div>
            <div className="flex flex-col items-start md:items-end">
              <h4 className="text-white font-black mb-8 md:mb-10 tracking-[0.3em] text-[10px] uppercase">Social Networks</h4>
              <div className="flex gap-4 md:gap-5">
                 {[Linkedin, Instagram].map((Icon, i) => (
                   <motion.a 
                    whileHover={{ scale: 1.15, y: -8 }}
                    key={i} 
                    href="#" 
                    className="p-4 md:p-5 rounded-2xl bg-white/5 text-gray-500 hover:text-white hover:bg-rose-600/20 transition-all duration-500 shadow-lg"
                   >
                     <Icon className="w-5 h-5 md:w-6 md:h-6" />
                   </motion.a>
                 ))}
              </div>
            </div>
          </div>
          <div className="pt-10 md:pt-12 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-8 md:gap-10">
            <p className="text-gray-600 text-[10px] md:text-[11px] font-bold tracking-widest uppercase text-center md:text-left">© 2025 Heliophania Anamorphe Pvt Ltd • All Rights Reserved</p>
            <div className="flex gap-8 md:gap-12">
              <button 
                onClick={() => setActiveLegalModal('privacy')}
                className="text-gray-600 hover:text-rose-500 text-[9px] md:text-[10px] font-black uppercase tracking-[0.3em] transition-colors"
              >
                Privacy Policy
              </button>
              <button 
                onClick={() => setActiveLegalModal('terms')}
                className="text-gray-600 hover:text-rose-500 text-[9px] md:text-[10px] font-black uppercase tracking-[0.3em] transition-colors"
              >
                Terms
              </button>
            </div>
          </div>
        </div>
      </footer>

      <Assistant />

      <LegalModal 
        isOpen={activeLegalModal === 'privacy'}
        onClose={() => setActiveLegalModal(null)}
        title="Privacy Protocol"
        content={privacyContent}
      />

      <LegalModal 
        isOpen={activeLegalModal === 'terms'}
        onClose={() => setActiveLegalModal(null)}
        title="Terms of Operation"
        content={termsContent}
      />
      
      <AnimatePresence>
        {formState === 'success' && (
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.5 }}
            className="fixed bottom-6 right-6 md:bottom-12 md:right-12 z-[2000] bg-rose-600 text-white px-8 py-6 md:px-12 md:py-8 rounded-[2rem] md:rounded-[3rem] font-black shadow-3xl flex items-center gap-4 md:gap-6 border border-white/10 backdrop-blur-3xl max-w-[90vw]"
          >
            <div className="bg-white/20 p-3 md:p-4 rounded-full flex-shrink-0">
              <CheckCircle className="w-6 h-6 md:w-10 md:h-10" />
            </div>
            <div>
              <p className="text-lg md:text-2xl tracking-tighter">Transmission Confirmed</p>
              <p className="text-[10px] md:text-sm font-bold opacity-70 tracking-wide uppercase">Team will contact you shortly.</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default App;